import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { fetchAccutechDeviceTypes } from '../api/devicesApi.js';

// Serial number patterns per V1 Resident.jsx lines 2469-2477
// accutech_device_id === 4 → alphanumeric
// all others               → 1-254 integer range
const SERIAL_PATTERN_ALPHA = /^[fF][0-9a-fA-F]{5}$/;
const SERIAL_PATTERN_RANGE = /^(?:[1-9]|0?[1-9]|0?[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-4])$/;
const ALPHA_TYPE_ID = 4;

/**
 * AddWanderingDeviceDrawer — slide-out drawer for adding an RF Tag (Accutech)
 * wandering device.
 *
 * Follows the same drawer pattern as ServerDrawer / AddDeviceDrawer: always-render
 * with `is-open` class toggle, form reset on open, Escape key close, focus first input.
 *
 * RF Tags use manufacture_id = 6 and have a simpler form than Inovonics devices:
 *   - No area/resident toggle or category selection
 *   - Device Type drives model (auto-set) and serial pattern
 *   - No alarm behavior options
 *
 * @param {{
 *   isOpen: boolean,
 *   onClose: () => void,
 *   onSave: (formData: object) => Promise<void>,
 *   existingDevices: Array,
 *   campusId: number,
 * }} props
 */
const AddWanderingDeviceDrawer = ({ isOpen, onClose, onSave, existingDevices, campusId }) => {
  const drawerRef = useRef(null);
  const tipRef = useRef(null);
  const [tipOpen, setTipOpen] = useState(false);
  const [tipStyle, setTipStyle] = useState({});

  // ---- Form state ----
  const [deviceTypeId, setDeviceTypeId] = useState('');
  const [serialNumber, setSerialNumber] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);

  // ---- Reference data ----
  const [deviceTypes, setDeviceTypes] = useState([]);
  const [typesLoading, setTypesLoading] = useState(false);

  // ---- Reset + load on open ----
  useEffect(() => {
    if (isOpen) {
      setDeviceTypeId('');
      setSerialNumber('');
      setDescription('');
      setSaving(false);
      setDeviceTypes([]);

      setTypesLoading(true);
      fetchAccutechDeviceTypes(campusId)
        .then((data) => setDeviceTypes(data || []))
        .catch(() => setDeviceTypes([]))
        .finally(() => setTypesLoading(false));
    }
  }, [isOpen, campusId]);

  // ---- Escape key handler ----
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // ---- Focus first input on open ----
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        const firstInput = drawerRef.current?.querySelector('select, input');
        firstInput?.focus();
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // ---- Selected type ----
  const selectedType = useMemo(() => {
    if (!deviceTypeId) return null;
    return deviceTypes.find(
      (t) => String(t.accutech_device_id) === String(deviceTypeId)
    ) || null;
  }, [deviceTypes, deviceTypeId]);

  // ---- Serial pattern based on device type ----
  const serialPattern = useMemo(() => {
    if (!deviceTypeId) return SERIAL_PATTERN_ALPHA; // default
    return String(deviceTypeId) === String(ALPHA_TYPE_ID)
      ? SERIAL_PATTERN_ALPHA
      : SERIAL_PATTERN_RANGE;
  }, [deviceTypeId]);

  const serialHint = useMemo(() => {
    if (!deviceTypeId) return '';
    return String(deviceTypeId) === String(ALPHA_TYPE_ID)
      ? '6 character hexidecimal starting with f'
      : 'Number between 1 and 254';
  }, [deviceTypeId]);

  // ---- Duplicate check ----
  const duplicateDevice = useMemo(() => {
    const trimmed = serialNumber.trim();
    if (!trimmed) return null;
    return (existingDevices || []).find(
      (d) => String(d.manufacture_uuid) === trimmed
    ) || null;
  }, [serialNumber, existingDevices]);

  // ---- Validation ----
  const isSerialValid = serialNumber.trim() !== '' && serialPattern.test(serialNumber.trim());

  const isFormValid =
    deviceTypeId &&
    isSerialValid &&
    !duplicateDevice &&
    !saving;

  // ---- Reset cascading fields on type change ----
  const handleTypeChange = useCallback((val) => {
    setDeviceTypeId(val);
    setSerialNumber('');
  }, []);

  // ---- Sort types alphabetically ----
  const sortedTypes = useMemo(() =>
    [...deviceTypes].sort((a, b) =>
      (a.description || '').localeCompare(b.description || '', undefined, { sensitivity: 'base' })
    ),
  [deviceTypes]);

  // ---- Submit ----
  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      if (!isFormValid) return;
      setSaving(true);

      // V1 pattern: manufacture_model_id = accutech_device_id (Resident.jsx:848)
      // V1 pattern: device_type_id = accutech_device_id
      // V1 pattern: device_name = description (selected type text)
      // V1 pattern: device_use_id = 0 (no alarm behavior for RF tags)
      const formData = {
        campus_id: campusId,
        manufacture_id: 6,
        manufacture_uuid: serialNumber.trim(),
        device_name: selectedType?.description || '',
        device_use_id: 0,
        is_active: 0,
        is_alerting_now: false,
        is_server: false,
        is_area_device: 0,
        is_resident: false,
        area_id: 0,
        resident_id: 0,
        manufacture_model_id: parseInt(deviceTypeId, 10),
        escalation_enabled: true,
        descriptions: description.trim(),
        device_type_id: parseInt(deviceTypeId, 10),
      };

      onSave(formData).finally(() => setSaving(false));
    },
    [isFormValid, campusId, serialNumber, selectedType, deviceTypeId, description, onSave]
  );

  return (
    <>
      {/* Backdrop */}
      {isOpen ? (
        <div className="dp-drawer-backdrop" onClick={onClose} aria-hidden="true" />
      ) : null}

      {/* Drawer panel */}
      <div
        ref={drawerRef}
        className={`dp-drawer ${isOpen ? 'is-open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Add Wandering Device"
      >
        {/* Header */}
        <div className="dp-drawer__header">
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <h2 className="dp-drawer__title">Add Wandering Device</h2>
            <span
              ref={tipRef}
              onMouseEnter={() => {
                if (!tipRef.current) return;
                const rect = tipRef.current.getBoundingClientRect();
                const spaceRight = window.innerWidth - rect.left;
                const spaceBelow = window.innerHeight - rect.bottom;
                setTipStyle({
                  position: 'fixed',
                  left: spaceRight < 320 ? undefined : `${rect.left}px`,
                  right: spaceRight < 320 ? '16px' : undefined,
                  top: spaceBelow < 120 ? undefined : `${rect.bottom + 6}px`,
                  bottom: spaceBelow < 120 ? `${window.innerHeight - rect.top + 6}px` : undefined,
                  maxWidth: '300px',
                  whiteSpace: 'normal',
                });
                setTipOpen(true);
              }}
              onMouseLeave={() => setTipOpen(false)}
              style={{ cursor: 'help', marginLeft: '8px', color: 'var(--color-text-muted)' }}
            >
              <i className="fe fe-info" style={{ fontSize: 'var(--font-size-sm)' }} />
              {tipOpen && (
                <span className="na-type-popover" style={tipStyle} role="tooltip">
                  <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', fontWeight: 'var(--font-weight-semibold)' }}>Adding Devices</span>
                  <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-body)', lineHeight: '1.45' }}>
                    Devices added to the system start as unassigned. To activate a device it must be assigned to a room or resident, which can be done via the Community &gt; Residents or Community &gt; Areas section of the portal.
                  </span>
                </span>
              )}
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="dp-drawer__close"
            aria-label="Close drawer"
          >
            <svg className="dp-icon-lg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="dp-drawer__body">
          {/* Device Type */}
          <div className="dp-drawer__field">
            <label htmlFor="wd-type" className="dp-drawer__label">
              Device Type <span className="dp-drawer__required">*</span>
            </label>
            <select
              id="wd-type"
              value={deviceTypeId}
              onChange={(e) => handleTypeChange(e.target.value)}
              className="dp-drawer__select"
              disabled={typesLoading}
              required
            >
              <option value="">
                {typesLoading ? 'Loading device types...' : 'Select device type...'}
              </option>
              {sortedTypes.map((t) => (
                <option key={t.accutech_device_id} value={t.accutech_device_id}>
                  {t.description}
                </option>
              ))}
            </select>
          </div>

          {/* Model (read-only, derived from selected type) */}
          {selectedType ? (
            <div className="dp-drawer__field">
              <label htmlFor="wd-model" className="dp-drawer__label">
                Model
              </label>
              <input
                id="wd-model"
                type="text"
                value={selectedType.Accutech_model_id || ''}
                className="dp-drawer__input"
                readOnly
                tabIndex={-1}
              />
            </div>
          ) : null}

          {/* Serial Number */}
          <div className="dp-drawer__field">
            <label htmlFor="wd-serial" className="dp-drawer__label">
              Serial Number <span className="dp-drawer__required">*</span>
            </label>
            <input
              id="wd-serial"
              type="text"
              value={serialNumber}
              onChange={(e) => setSerialNumber(e.target.value)}
              placeholder={serialHint || 'Enter serial number'}
              className={`dp-drawer__input ${(serialNumber && !isSerialValid) || duplicateDevice ? 'is-invalid' : ''}`}
              disabled={!deviceTypeId}
              required
            />
            {serialNumber && !isSerialValid ? (
              <p className="dp-drawer__error">
                {String(deviceTypeId) === String(ALPHA_TYPE_ID)
                  ? 'Enter a valid alphanumeric serial number'
                  : 'Enter a number between 1 and 254'}
              </p>
            ) : null}
            {duplicateDevice ? (
              <p className="dp-drawer__error">A device with this serial number already exists in the system</p>
            ) : null}
          </div>

          {/* Description (optional) */}
          <div className="dp-drawer__field">
            <label htmlFor="wd-description" className="dp-drawer__label">
              Description
            </label>
            <input
              id="wd-description"
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional description"
              className="dp-drawer__input"
            />
          </div>

          {/* Footer */}
          <div className="dp-drawer__footer">
            <button
              type="button"
              onClick={onClose}
              className="dp-btn dp-btn--secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!isFormValid || saving}
              className="dp-btn dp-btn--primary"
            >
              {saving ? 'Adding...' : 'Add Device'}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default AddWanderingDeviceDrawer;
